import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, X, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";
import {
  getPendingRecovery,
  markConsumed,
  RECOVERY_WINDOW_MS,
} from "@/lib/notificationRecovery";

/**
 * MissedNotificationBanner
 * ------------------------
 * Persistent bottom banner that offers to re-open a notification the user
 * *just* tapped but never actually saw. See :file:`lib/notificationRecovery.js`
 * for the full motivation. Rendered inside :file:`PublicShell` so it
 * appears on every public route.
 *
 * Behavior
 * ~~~~~~~~
 * * On every URL change we call :func:`getPendingRecovery` — if it returns
 *   a record, we render the banner. If it returns ``null`` (record is
 *   consumed / stale / matches the current path), the banner stays hidden.
 * * The banner sits just above the bottom tab bar so it never obscures
 *   the header or primary hero content. Uses Framer Motion for a gentle
 *   slide-in so it doesn't yank the user's attention violently.
 * * "View" navigates to the recorded ``path + search``, which includes
 *   any deep-link params (``?news=``, ``?broadcast=``) needed by the
 *   destination component to open its detail modal.
 * * A background timer re-checks the record every 30 s so a banner that
 *   was showing while the 10-minute window elapsed disappears on its own.
 */
export function MissedNotificationBanner() {
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [tick, setTick] = useState(0);

  const record = useMemo(
    () => getPendingRecovery(location.pathname),
    // ``tick`` forces a re-check when the 30-second timer fires.
    [location.pathname, tick] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Periodic re-check so an on-screen banner disappears once the 10 min
  // window elapses (otherwise the banner would stay visible even after
  // the record went stale).
  useEffect(() => {
    if (!record) return undefined;
    const id = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [record]);

  if (!record) return null;

  const copy = t.missedNotification || {};
  const bodyByType = {
    news: copy.bodyNews,
    broadcast: copy.bodyBroadcast,
    event: copy.bodyEvent,
    prayer: copy.bodyPrayer,
  };
  const body = bodyByType[record.type] || copy.bodyNews;

  const onView = () => {
    // Navigate to the tapped route — the destination component (HomePage
    // for news, BroadcastModal for broadcast, EventsPage / PrayersPage
    // for the others) will handle opening the deep-linked modal.
    navigate(
      { pathname: record.path, search: record.search || "" },
      { replace: false }
    );
    markConsumed();
    // Bump tick so the banner disappears immediately without waiting for
    // the location.pathname effect to observe the new consumed state.
    setTick((n) => n + 1);
  };

  const onDismiss = () => {
    markConsumed();
    setTick((n) => n + 1);
  };

  return (
    <AnimatePresence>
      {/* Positioning wrapper: static fixed layout so Framer Motion's
          internal transform (used for `y` and `opacity` animation) does
          NOT overwrite our own `translateX(-50%)` centering. Without this
          split, the banner slides in un-centered on narrow viewports and
          the "View" button ends up clipped off the right edge. */}
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: "calc(64px + env(safe-area-inset-bottom))",
          zIndex: 60,
          display: "flex",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <motion.div
          key="missed-notification-banner"
          initial={{ y: 32, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 24, opacity: 0 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
          style={{
            width: "min(96vw, 26rem)",
            pointerEvents: "auto",
          }}
          data-testid="missed-notification-banner"
        >
        <div
          className="rounded-2xl border border-primary/25 bg-white/95 shadow-xl backdrop-blur ring-1 ring-black/5 p-3"
        >
          <div className="flex items-start gap-3">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Bell className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <p
                className="font-serif-display text-sm font-semibold leading-snug text-foreground"
                data-testid="missed-notification-title"
              >
                {copy.title}
              </p>
              <p
                className="mt-0.5 text-[12px] text-muted-foreground leading-snug"
                data-testid="missed-notification-body"
              >
                {body}
              </p>
            </div>
            <button
              type="button"
              onClick={onDismiss}
              aria-label={copy.dismissCta || "Dismiss"}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted/80 focus-visible:ring-2 focus-visible:ring-primary/40"
              data-testid="missed-notification-dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-2 flex justify-end">
            <Button
              size="sm"
              onClick={onView}
              className="rounded-full gap-1"
              data-testid="missed-notification-view"
            >
              {copy.viewCta || "View"}
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </motion.div>
      </div>
    </AnimatePresence>
  );
}

// Re-export the window so consumers can display "Xm ago" style hints if
// they want to expand this later.
export { RECOVERY_WINDOW_MS };

export default MissedNotificationBanner;
